import * as React from 'react';

import cn from 'classnames';

import { getConfig } from '@constants';

import { EMediaDataTypes } from '@enums';

import { Translate } from '@decorators';

import { File as FileItem } from '@common/components/File';
import { FileButton } from '@common/components/ui';
import { getRandomStr, getFormattedFileSize, noop } from '@common/utils';

import { IFile, IUploadingFile } from '@interfaces';

import { IFileUploaderProps, IFileUploaderState } from './types';

import * as s from './FileUploader.scss';

@Translate('common.components.FileUploader')
export class FileUploader extends React.Component<IFileUploaderProps, IFileUploaderState> {

	static defaultProps: Partial<IFileUploaderProps> = {
		maxFileSize: getConfig().fileUploader.maxFileSize,
		maxFilesCount: getConfig().fileUploader.maxFilesCount,
		isMultipleUpload: true,
		onFileUploadCancel: noop,
		onFileUploadError: noop,
	};

	state: IFileUploaderState = {
		isMaxFilesCountError: false,
		isMaxFileSizeError: false,
	};

	getFile(file: File): IFile {
		return {
			id: null,
			uploadId: getRandomStr(),
			type: EMediaDataTypes.FILE,
			title: file.name,
			data: file,
		};
	}

	onFileButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const {
			target: {
				files: selectedFiles,
			},
		} = event;
		const {
			files,
			maxFilesCount,
			maxFileSize,
			onFilesAdd,
			onFileUpload,
		} = this.props;

		const filesForUpload = Array.from(selectedFiles)
			.filter((file) => file.size <= maxFileSize)
			.map((file) => this.getFile(file));

		if (filesForUpload.length < selectedFiles.length) {
			this.setState(
				{
					isMaxFileSizeError: true,
				},
				() => window.setTimeout(
					() => this.setState({
						isMaxFileSizeError: false,
					}),
					2000,
				),
			);
		}

		if (filesForUpload.length + files.length > maxFilesCount) {
			this.setState({
				isMaxFilesCountError: true,
			});

			onFilesAdd(filesForUpload);
		} else {
			filesForUpload.forEach((file) => onFileUpload(file));
		}
	};

	onFileRemove = (file: IUploadingFile) => {
		const {
			files,
			maxFilesCount,
			onFileRemove,
			onFileUpload,
		} = this.props;

		onFileRemove(file.data);

		if (files.length - 1 <= maxFilesCount) {
			this.setState({
				isMaxFilesCountError: false,
			});

			files
				.filter(({
					data: {
						uploadId,
					},
					meta: {
						isFetching,
						isFetched,
					},
				}) => !isFetching && !isFetched && uploadId !== file.data.uploadId)
				.forEach(({ data }) => onFileUpload(data));
		}
	};

	onFileUploadCancel = () => {
		const {
			onFileUploadCancel,
		} = this.props;

		onFileUploadCancel();
	};

	onFileUploadSuccess = () => {
		const {
			onFileUploadSuccess,
		} = this.props;

		onFileUploadSuccess();
	};

	onFileUploadError = (error: Error) => {
		const {
			onFileUploadError,
		} = this.props;

		onFileUploadError(error);
	};

	render() {
		const {
			files,
			isMultipleUpload,
			title,
			maxFileSize,
			maxFilesCount,
			isFilesNotReadyError,
			classNames: {
				block: blockClassName,
				button: buttonClassName,
			} = {},
		} = this.props;
		const {
			isMaxFileSizeError,
			isMaxFilesCountError,
		} = this.state;

		return (
			<div className={cn(s.block, blockClassName)}>
				{files.map((file) => (
					<FileItem
						key={file.data.uploadId}
						file={file}
						onRemove={this.onFileRemove}
						onUploadCancel={this.onFileUploadCancel}
						onUploadError={this.onFileUploadError}
						onUploadSuccess={this.onFileUploadSuccess}
					/>
				))}

				<FileButton
					isMultipleUpload={isMultipleUpload}
					isValid={!isMaxFileSizeError && !isMaxFilesCountError}
					className={cn(s.fileButton, buttonClassName)}
					title={title}
					onChange={this.onFileButtonChange}
				/>

				{isFilesNotReadyError &&
					<div className={s.error}>
						{this.t('filesNotReadyError')}
					</div>
				}

				{isMaxFileSizeError &&
					<div className={s.error}>
						{this.t('maxFileSizeError', { maxFileSize: getFormattedFileSize(maxFileSize) })}
					</div>
				}

				{isMaxFilesCountError &&
					<div className={s.error}>
						{this.t('maxFilesCountError', { maxFilesCount })}
					</div>
				}
			</div>
		);
	}

}
